import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
    catchError,
    delay,
    filter,
    finalize,
    fromEvent,
    throttleTime,
    throwError,
    timeout,
    TimeoutError
} from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { AlertService } from '@core/services/alert/alert.service';
import { BrowserService } from '@services/browser.service';
import { GlobalErrorService } from '@services/GlobalErrorService/global-error.service';
import { GlobalErrorType } from '@models/GlobalErrorPayload';
import { AuthAPIService } from '@services/AuthService/auth.api.service';
import { SettingAPIService } from '@services/SettingService/setting.api.service';
import { MenuService } from '@services/MenuBaseService/menu-base.service';
import { AppSettings } from '@models/Settings.model';
import { SplashScreenService } from './splash-screen.service';
import { SessionService } from '@core/services/session/session.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [ConfirmDialogModule, CommonModule, RouterModule, ToastModule, DialogModule],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    protected appInitialized = false;

    /** Outside forced triggers (boot, login), app data refreshes at most this often. */
    private static readonly REFRESH_STALE_MS = 5 * 60 * 1000;
    private lastRefreshAt = 0;
    private previousUrl = '';

    private splashScreen = inject(SplashScreenService);

    constructor(
        private titleService: Title,
        private sessionService: SessionService,
        public alertService: AlertService,
        private authAPIService: AuthAPIService,
        private settingsService: SettingAPIService,
        private menuService: MenuService,
        private browserService: BrowserService,
        private globalErrorService: GlobalErrorService,
        private router: Router,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.titleService.setTitle(this.sessionService.getCompanyName() ?? '');

        this.globalErrorService.onError$
            .pipe(throttleTime(2000))
            .subscribe(({ type, status, title, message }) => {
                // Alert
                if (type === GlobalErrorType.Alert) {
                    if (status === 422) {
                        alertService.showWarn(title, message);
                    } else {
                        alertService.showError(title, message);
                    }
                }

                // TODO: future expansion - dialog error.
                if (type == GlobalErrorType.Dialog) {
                    this.authAPIService.logout();
                }
            });

        // Refresh settings + profile on first load, after a login, or when data
        // has gone stale — plain navigation in between costs no requests.
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
                const cameFromLogin = this.previousUrl.startsWith('/login');
                this.previousUrl = (event as NavigationEnd).urlAfterRedirects;
                if (this.lastRefreshAt === 0 || cameFromLogin || this.isStale()) {
                    this.refreshAppData();
                }
            });

        // Recover from stale chunk errors after a deployment.
        // When a lazy-loaded chunk no longer exists on the server (new deploy
        // hashes), Angular throws "Failed to fetch dynamically imported module".
        // Forcing a full navigation reloads the fresh index.html + chunk manifest
        // so the user transparently lands on the page they were trying to reach.
        this.router.events
            .pipe(filter((event) => event instanceof NavigationError))
            .subscribe((event) => {
                const navError = event as NavigationError;
                if (this.isChunkLoadError(navError.error)) {
                    window.location.assign(navError.url);
                }
            });
    }

    ngOnInit(): void {
        if (!this.browserService.isBrowser) return;

        // Returning to the tab after a while picks up flag/menu changes without a click.
        fromEvent(this.document, 'visibilitychange').subscribe(() => {
            if (this.document.visibilityState === 'visible' && this.isStale()) {
                this.refreshAppData();
            }
        });
    }

    private isStale(): boolean {
        return Date.now() - this.lastRefreshAt > AppComponent.REFRESH_STALE_MS;
    }

    private refreshAppData(): void {
        this.lastRefreshAt = Date.now();
        this.loadAppSettings();
        // Skips itself when logged out; doubles as a token-validity check.
        this.authAPIService.fetchUserProfile();
    }

    private isChunkLoadError(error: unknown): boolean {
        if (!(error instanceof Error)) return false;
        return (
            error.message.includes('Failed to fetch dynamically imported module') ||
            error.message.includes('Importing a module script failed') ||
            error.name === 'ChunkLoadError'
        );
    }

    private loadAppSettings() {
        const favicon = this.document.getElementById('app-favicon') as HTMLLinkElement;
        if (favicon) {
            favicon.href = 'assets/images/logos/default_logo.png';
        }

        this.settingsService
            .fetchSettings()
            .pipe(
                timeout(5000),
                delay(200),
                catchError((err) => throwError(() => err)),
                finalize(() => {
                    this.splashScreen.dismiss();
                    this.appInitialized = true;
                })
            )
            .subscribe({
                next: (res) => {
                    const settingsArray = res.body?.result;
                    if (settingsArray) {
                        this.applySettings(settingsArray);
                    } else {
                        console.warn('No settings found in the database');
                    }
                },
                error: (err) => {
                    if (!(err instanceof TimeoutError)) {
                        this.alertService.showError('Failed to load settings. Please contact IT.');
                    }
                    console.error('Failed to load settings', err);
                }
            });
    }

    private applySettings(settings: AppSettings) {
        this.menuService.refreshMenu();

        // Set company name
        if (settings.appearance.companyName) {
            this.titleService.setTitle(settings.appearance.companyName || '');
        }

        // Set favicon
        if (settings.appearance.companyFaviconUrl) {
            const favicon = this.document.getElementById('app-favicon') as HTMLLinkElement;
            if (favicon) {
                favicon.href =
                    settings.appearance.companyFaviconUrl || 'assets/images/logos/default_logo.png';
            }
        }
    }
}
